import React, { useState, useEffect } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';
import VideoUpload from './VideoUpload';
import VideoList from './VideoList';
import VideoPlayer from './VideoPlayer';
import './Videos.css';

const Videos = () => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  useEffect(() => {
    fetchVideos();
  }, [refreshTrigger]);

  const fetchVideos = async () => {
    setLoading(true);
    try {
      console.log('Attempting to fetch videos from API...');
      
      // Get the current auth session to include the token in the request
      const { idToken } = (await fetchAuthSession()).tokens ?? {};
      
      if (!idToken) {
        throw new Error('No authentication token available');
      }
      
      // Use fetch directly with the API URL from aws-exports
      const apiUrl = 'https://f8r84a0hqe.execute-api.us-east-1.amazonaws.com/prod/videos';
      console.log('Fetching from URL:', apiUrl);
      
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Authorization': `${idToken.toString()}`
        }
      });
      
      console.log('API Response status:', response.status);
      
      if (!response.ok) {
        throw new Error(`API returned status ${response.status}`);
      }
      
      const data = await response.json();
      console.log('Parsed response data:', data);
      
      if (data && Array.isArray(data)) {
        setVideos(data);
      } else {
        console.log('Response is not an array, setting empty videos array');
        setVideos([]);
      }
      setError(null);
    } catch (err) {
      console.error('Error fetching videos:', err);
      // Log more detailed error information
      if (err.response) {
        console.error('Error response:', err.response);
      }
      if (err.message) {
        console.error('Error message:', err.message);
      }
      setError('Failed to load videos. Please try again later.');
      setVideos([]);
    } finally {
      setLoading(false);
    }
  };

  const handleVideoSelect = (video) => {
    setSelectedVideo(video);
  };

  const handleUploadSuccess = () => {
    // Trigger a refresh of the video list
    setRefreshTrigger(prev => prev + 1);
  };

  const handleDeleteVideo = async (videoId) => {
    try {
      console.log('Attempting to delete video:', videoId);
      
      // Get the current auth session to include the token in the request
      const { idToken } = (await fetchAuthSession()).tokens ?? {};
      
      if (!idToken) {
        throw new Error('No authentication token available');
      }
      
      // Use the API directly with the videoId as a query parameter
      const apiUrl = `https://f8r84a0hqe.execute-api.us-east-1.amazonaws.com/prod/videos?videoId=${videoId}`;
      
      console.log('Sending DELETE request to:', apiUrl);
      
      const response = await fetch(apiUrl, {
        method: 'DELETE',
        headers: {
          'Authorization': `${idToken.toString()}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error(`API returned status ${response.status}`);
      }
      
      console.log('Video deleted successfully');
      
      // If the deleted video was selected, clear the selection
      if (selectedVideo && selectedVideo.videoId === videoId) {
        setSelectedVideo(null);
      }
      
      // Refresh the video list
      setRefreshTrigger(prev => prev + 1);
    } catch (err) {
      console.error('Error deleting video:', err);
      if (err.response) {
        console.error('Error response:', err.response);
      }
      if (err.message) {
        console.error('Error message:', err.message);
      }
      alert('Failed to delete video. Please try again.');
    }
  };

  return (
    <div className="videos-container">
      <h2>Videos</h2>
      
      <div className="videos-content">
        <div className="videos-sidebar">
          <VideoUpload onUploadSuccess={handleUploadSuccess} />
          
          <div className="videos-list-container">
            <h2>Your Videos</h2>
            {loading ? (
              <p>Loading videos...</p>
            ) : error ? (
              <p className="error-message">{error}</p>
            ) : (
              <VideoList 
                videos={videos} 
                onVideoSelect={handleVideoSelect} 
                onDeleteVideo={handleDeleteVideo}
                selectedVideoId={selectedVideo?.id}
              />
            )}
          </div>
        </div>
        
        <div className="video-player-container">
          {selectedVideo ? (
            <VideoPlayer video={selectedVideo} />
          ) : (
            <div className="no-video-selected">
              <p>Select a video to play or upload a new one</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Videos;
